import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  FinanceCustomCategoryLegalEntityFragment,
  FinanceValueObjectsLedgerAccountOperationTypeEnum,
  UpdateFinanceTaxTypeLedgerAccountAssignmentsInput,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { LedgerAccountSelector } from 'modules/banking/components/LedgerAccountSelector'
import { LedgerAccountAssignmentInput } from 'modules/finance/hooks/accounting/useLedgerAccountAssignment'

import NoLedgerAccountsCallout from '../NoLedgerAccountsCallout'

interface Props {
  legalEntities: FinanceCustomCategoryLegalEntityFragment[]
  currentAssignments?: LedgerAccountAssignmentInput[]
  loading?: boolean
  onChange: (inputData: LedgerAccountAssignmentInput) => void
  withOperationType?: boolean
  seeSalesLedgerAccounts?: boolean
  seePurchaseLedgerAccounts?: boolean
}

const LedgerAccountAssigmentForm = ({
  legalEntities,
  currentAssignments,
  onChange,
  withOperationType = false,
  seeSalesLedgerAccounts = false,
  seePurchaseLedgerAccounts = false,
}: Props) => {
  const showCallout = legalEntities.some(
    (legalEntity) => legalEntity.financeAccountsConnection.totalCount === 0
  )

  const emptyAssignment = (
    legalEntityId: number,
    operationType: FinanceValueObjectsLedgerAccountOperationTypeEnum | undefined
  ) => {
    return {
      legalEntityId: legalEntityId,
      ledgerAccountId: 0,
      ledgerAccountResourceId: null,
      operationType: operationType,
    }
  }
  const matchingAssignment = (
    legalEntityId: number,
    operationType: FinanceValueObjectsLedgerAccountOperationTypeEnum | undefined
  ) => {
    return currentAssignments?.find(
      (assignment) =>
        assignment.legalEntityId === legalEntityId && assignment.operationType == operationType
    )
  }

  const assignmentToAdd = (
    legalEntityId: number,
    operationType: FinanceValueObjectsLedgerAccountOperationTypeEnum | undefined
  ) => {
    const assignment = matchingAssignment(legalEntityId, operationType)
    if (assignment) {
      return assignment
    } else {
      return emptyAssignment(legalEntityId, operationType)
    }
  }

  const legalEntitesWithAssignments = legalEntities.map((legalEntity) => {
    const matchingAssignments: UpdateFinanceTaxTypeLedgerAccountAssignmentsInput[] = []
    if (withOperationType) {
      if (seePurchaseLedgerAccounts) {
        // we need to make sure purchase goes first
        matchingAssignments.unshift(assignmentToAdd(legalEntity.id, 'purchase'))
      }
      if (seeSalesLedgerAccounts) {
        matchingAssignments.push(assignmentToAdd(legalEntity.id, 'sale'))
      }
    } else {
      matchingAssignments.push(assignmentToAdd(legalEntity.id, undefined))
    }

    return {
      ...legalEntity,
      accountResources: matchingAssignments.length > 0 ? matchingAssignments : null,
    }
  })

  const bothSelectors = withOperationType && seePurchaseLedgerAccounts && seeSalesLedgerAccounts

  return (
    <Box gap="s24">
      <Box gap="s8">
        <Box flexDirection="row" gap="s8" justifyContent="spaceBetween">
          <Box width="s140">
            <Text.Normal weight="medium" size="200" color="grey1000" ellipsis>
              {i18n.t(
                'finance.settings.accounting.custom_categories.modals.create.fields.legal_entity'
              )}
            </Text.Normal>
          </Box>
          <Box width="s240" flexDirection="row">
            {!withOperationType && (
              <Text.Normal weight="medium" size="200" color="grey1000" ellipsis>
                {i18n.t(
                  'finance.settings.accounting.custom_categories.modals.create.fields.account'
                )}
              </Text.Normal>
            )}
            {withOperationType && seePurchaseLedgerAccounts && (
              <Text.Normal weight="medium" size="200" color="grey1000" ellipsis>
                {bothSelectors
                  ? i18n.t(
                      'finance.settings.accounting.custom_categories.modals.create.fields.purchase_account'
                    )
                  : i18n.t(
                      'finance.settings.accounting.custom_categories.modals.create.fields.account'
                    )}
              </Text.Normal>
            )}
            {withOperationType && seeSalesLedgerAccounts && (
              <Text.Normal weight="medium" size="200" color="grey1000" ellipsis>
                {bothSelectors
                  ? i18n.t(
                      'finance.settings.accounting.custom_categories.modals.create.fields.sale_account'
                    )
                  : i18n.t(
                      'finance.settings.accounting.custom_categories.modals.create.fields.account'
                    )}
              </Text.Normal>
            )}
          </Box>
        </Box>

        {legalEntitesWithAssignments.map((legalEntity) => (
          <Box
            key={legalEntity.id}
            flexDirection="row"
            gap="s8"
            justifyContent="spaceBetween"
            alignItems="center"
          >
            <Box width="s140">
              <Text.Normal size="200">{legalEntity.legalName}</Text.Normal>
            </Box>
            <Box width="s240">
              {(legalEntity.financeAccountsConnection?.totalCount || 0) > 0 ? (
                <Box flexDirection="row" gap="s8">
                  {legalEntity.accountResources?.map((accountResource, index) => (
                    <LedgerAccountSelector
                      key={index}
                      legalEntityId={legalEntity.id}
                      selectedLedgerAccountIdProp={accountResource.ledgerAccountId}
                      disabled={false}
                      onSelect={(selectedAccountId) => {
                        onChange({
                          ledgerAccountId: selectedAccountId,
                          ledgerAccountResourceId: accountResource?.ledgerAccountResourceId,
                          legalEntityId: legalEntity.id,
                          operationType: accountResource.operationType,
                        })
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Box flexDirection="row" gap="s4" alignItems="center" minHeight="s40">
                  <Box flexNoShrink>
                    <Icon.Medium icon="alert-circle" color="grey800" />
                  </Box>
                  <Text weight="medium" size="200" color="grey1000">
                    {i18n.t(
                      'finance.settings.accounting.custom_categories.modals.alerts.no_ledger_accounts'
                    )}
                  </Text>
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>
      {showCallout && <NoLedgerAccountsCallout />}
    </Box>
  )
}

export default LedgerAccountAssigmentForm
